import { finalizeBasket, getCheckPrice, reconstructBasket, sendBasket, updateTenantData } from 'containers/OrderDetails/basketSlice'
import { addNotification } from 'containers/Notifications/notificationsSlice'
import { selectIsLoading, selectUserId } from 'containers/Login/loginSlice'
import SelectUserToCartPopup from './SelectUserToCartPopup'
import { useReduxDispatch, useReduxSelector } from 'store'
import { useHistory, useParams } from 'react-router-dom'
import ArrowBtn from 'components/buttons/ArrowBtn'
import React, { useEffect, useState } from 'react'
import useTranslation from 'hooks/useTranslation'
import usePopupState from 'hooks/usePopupState'
import Loading from 'components/Loading'
import { GET, POST } from 'rest/request'
import useLogout from 'hooks/useLogout'
import * as urls from 'rest/urls'
import './style.scss'

interface User {
  address: string
  city: string
  email: string
  id: string
  name: string
  object: string
  pesel: string
  zip: string
}
interface Company {
  address: string
  id: string
  name: string
  object: string
  nip: string
}

type LinkErrorReason = 'expired' | 'invalid'

interface LinkError {
  reason: LinkErrorReason
  message: string
}

const getLinkError = (error: unknown): LinkError | null => {
  try {
    const data = JSON.parse(String(error))
    if (data?.reason === 'expired' || data?.reason === 'invalid') {
      return { reason: data.reason, message: typeof data.message === 'string' ? data.message : '' }
    }
  } catch { }
  return null
}

export interface UserAndCompanyData {
  user: User
  company?: Company
}

const RestoreOrder: React.FC = () => {
  const userId = useReduxSelector(selectUserId)
  const isLoading = useReduxSelector(selectIsLoading)
  const [userData, setUserData] = useState<User | null>(null)
  const [linkError, setLinkError] = useState<LinkError | null>(null)
  const { popup, openPopup } = usePopupState<{ type: 'selectUserToCart', props: UserAndCompanyData }>()
  const { cartId: linkToken } = useParams<{ cartId: string }>()

  const dispatch = useReduxDispatch()
  const { t } = useTranslation('restore')
  const { replace, goBack } = useHistory()
  const logout = useLogout()

  const getUserData = async (props: UserAndCompanyData) => {
    try {
      const response = await GET(urls.userInfo)
      if (response.data) {
        setUserData(response.data)
        openPopup({ type: 'selectUserToCart', props })
      } else {
        throw new Error('Wrong response')
      }

    } catch (err) {
      console.warn('Fetch: failed')
      openPopup({ type: 'selectUserToCart', props })
    }
  }

  const resetCart = () => {
    dispatch(addNotification({
      type: 'info',
      message: t('weCouldNotLoadYourPreparedCart')
    }))
    dispatch(finalizeBasket(undefined))
    replace('/')
  }

  const initialBasket = async (redirect = '/order/summary') => {
    dispatch(sendBasket({ step: 'rentingSummary' })).then((basketRes) => {
      if (basketRes.meta.requestStatus === 'fulfilled') {
        if (basketRes.payload?.isFinished || basketRes.payload?.sold) {
          dispatch(finalizeBasket(undefined))
          replace('/dashboard')

        } else {
          replace(redirect)
        }

      } else {
        resetCart()
      }
    })
  }

  const restoreFromLink = async (token: string) => {
    try {
      const response = await POST(`${urls.cart}/${encodeURIComponent(token)}/finalize`, {}, false)
      if (!response.payload) {
        dispatch(addNotification({
          type: 'info',
          message: t('cartIsNoLongerActive')
        }))
        replace('/')
        return
      }

      dispatch(reconstructBasket(response))
      const user: basic.Form = {
        id: response.user?.id || ''
      }
      if (response.company?.id) {
        user.companyId = response.company.id
      }

      dispatch(getCheckPrice(user)).then((checkPriceRes) => {
        if (checkPriceRes.meta.requestStatus === 'fulfilled') {
          if (response.isPaid) {
            if (!response.user) {
              dispatch(addNotification({
                type: 'info',
                message: t('invalidLink')
              }))
              resetCart()

            } else {
              if (userId && userId !== response.user.id) {
                logout()
              }
              dispatch(updateTenantData({
                user: response.user,
                company: response.company
              }))
              replace('/order/summary')
            }

          } else if (!response.user) {
            dispatch(addNotification({
              type: 'info',
              message: t('youMustSelectAccountToContinueRental')
            }))
            initialBasket('/login')

          } else if (userId && userId !== response.user.id) {
            getUserData({
              user: response.user,
              company: response.company
            })

          } else {
            dispatch(updateTenantData({
              user: response.user,
              company: response.company
            }))
            initialBasket()
          }

        } else {
          resetCart()
        }
      })

    } catch (error) {
      const linkErr = getLinkError(error)
      if (linkErr) {
        setLinkError(linkErr)
        return
      }
      resetCart()
    }
  }

  useEffect(() => {
    if (isLoading) return
    if (linkToken) restoreFromLink(linkToken)
    else replace('/')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkToken, isLoading])

  if (linkError) {
    return (
      <div className='restoreOrder__inactive'>
        <div className='restoreOrder__inactive__content'>
          <i className='icon icon--logo' />
          <div className='restoreOrder__inactive__title'>
            {t(linkError.reason === 'expired' ? 'linkExpired' : 'invalidLink')}
          </div>
          <div className='restoreOrder__inactive__desc'>
            {linkError.message || t('askSupportForNewLink')}
          </div>
          <ArrowBtn
            enlarged
            text={t('goToHomePage')}
            action={() => {
              replace('/')
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <>
      {popup?.type === 'selectUserToCart' && popup.props && <SelectUserToCartPopup
        data={popup.props}
        userData={userData}
        callback={(saveTenantData) => {
          if (saveTenantData) {
            dispatch(updateTenantData(popup.props))
          }
          goBack()
          initialBasket()
        }}
      />}

      <Loading />
    </>
  )
}

export default RestoreOrder